import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { FileText, Upload, Loader2, Clock, X, FileCheck2 } from "lucide-react";
import { api, errMsg, money, fdate, fileToDataURL } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { PageHeader, StatusBadge, Empty, Loading, Timeline, LegalPanel, Hash, DemoBadge, Row } from "@/components/common";
import CheckoutDialog from "@/components/CheckoutDialog";
import { DynamicFields } from "@/components/community";

const TL = { iniciado: "Iniciado", pago_pendiente: "Pago pendiente", en_revision: "En revisión", emitido: "Emitido", rechazado: "Rechazado" };

export default function Tramites() {
  const cat = useFetch("/tramites/catalog");
  const mine = useFetch("/tramites/mine", { poll: 6000 });
  const [sel, setSel] = useState(null);
  const [fields, setFields] = useState({});
  const [docs, setDocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [checkout, setCheckout] = useState(null);

  const open = (t) => { setSel(t); setFields({}); setDocs([]); };
  const addDocs = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (f.size > 5_000_000) { toast.error(`${f.name} excede 5 MB`); continue; }
      const b64 = await fileToDataURL(f);
      setDocs((d) => [...d, { name: f.name, mime: f.type, content_b64: b64, size: f.size }]);
    }
    e.target.value = "";
  };
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/tramites", { type: sel.id, fields, documents: docs.map(({ size, ...d }) => d) });
      toast.success(`Expediente ${data.folio} creado`);
      setSel(null);
      mine.reload();
      setCheckout({ kind: "tramite", ref: data.folio });
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const current = detail ? (mine.data || []).find((e) => e.folio === detail) : null;

  return (
    <div>
      <PageHeader eyebrow="Ventanilla digital" title="Trámites en línea" subtitle="Integra tu expediente digital, paga con Cuenta Nu y recibe tu documento firmado y sellado." />
      <h2 className="text-lg font-semibold mb-3">Catálogo</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        {cat.loading && <Loading rows={2} />}
        {(cat.data?.tramites || []).map((t) => (
          <Card key={t.id} className="card-hover">
            <CardContent className="p-5 flex flex-col h-full">
              <div className="flex items-start justify-between gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <span className="font-mono text-sm font-medium">{money(t.cost)}</span>
              </div>
              <div className="mt-3 font-medium">{t.label}</div>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Clock className="h-3 w-3" />Resolución estimada: {t.days} día(s)</div>
              <div className="text-xs text-muted-foreground mt-1">Requisitos: {t.docs.join(", ")}</div>
              <Button className="mt-4 w-full btn-press" variant="secondary" onClick={() => open(t)} data-testid={`tramite-start-${t.id}`}>Iniciar trámite</Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <h2 className="text-lg font-semibold mb-3">Mis expedientes</h2>
      <Card><CardContent className="p-4 space-y-2">
        {mine.loading ? <Loading /> : !mine.data?.length ? <Empty title="Sin expedientes" hint="Inicia un trámite del catálogo." /> : mine.data.map((e) => (
          <div key={e.folio} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 hover:bg-secondary/60 transition-colors" data-testid={`expediente-row-${e.folio}`}>
            <button className="text-left min-w-0" onClick={() => setDetail(e.folio)}>
              <div className="font-mono text-xs">{e.folio}</div>
              <div className="font-medium text-sm">{e.tramite_label} {e.is_demo && <DemoBadge />}</div>
              <div className="text-xs text-muted-foreground">{fdate(e.created_at)} · {e.documents?.length} documento(s)</div>
            </button>
            <div className="flex items-center gap-2">
              <StatusBadge status={e.status} />
              {e.status === "pago_pendiente" && <Button size="sm" onClick={() => setCheckout({ kind: "tramite", ref: e.folio })} data-testid={`expediente-pay-${e.folio}`}>Pagar {money(e.cost)}</Button>}
              <Button size="sm" variant="outline" onClick={() => setDetail(e.folio)} data-testid={`expediente-open-${e.folio}`}>Ver</Button>
            </div>
          </div>
        ))}
      </CardContent></Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="bg-white sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="tramite-form-dialog">
          <DialogHeader><DialogTitle>{sel?.label}</DialogTitle><DialogDescription>Derechos: {money(sel?.cost)} · cada documento se registra con su huella SHA-256.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {sel?.fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={f.key}>{f.label}</Label>
                <Input id={f.key} value={fields[f.key] || ""} onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })} data-testid={`tramite-field-${f.key}`} />
              </div>
            ))}
            {sel?.custom_fields?.length > 0 && <DynamicFields fields={sel.custom_fields} values={fields} onChange={setFields} prefix="tramite-custom" />}
            <div className="space-y-1.5">
              <Label>Documentos ({sel?.docs.join(", ")})</Label>
              <label className="flex items-center gap-3 rounded-lg border-2 border-dashed p-3 cursor-pointer hover:bg-secondary transition-colors">
                <Upload className="h-4 w-4 text-primary" /><span className="text-sm">Adjuntar archivos (PDF/imagen, máx. 5 MB)</span>
                <input type="file" multiple className="hidden" onChange={addDocs} data-testid="tramite-docs-input" />
              </label>
              {docs.map((d, i) => (
                <div key={i} className="flex items-center justify-between rounded-md bg-secondary px-3 py-1.5 text-xs">
                  <span className="truncate">{d.name} · {(d.size / 1024).toFixed(0)} KB</span>
                  <button onClick={() => setDocs(docs.filter((_, j) => j !== i))} aria-label="Quitar"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
            <Button className="w-full" onClick={submit} disabled={saving} data-testid="tramite-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Crear expediente y pagar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Sheet open={!!current} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="bg-white w-full sm:max-w-lg overflow-y-auto" data-testid="expediente-detail-sheet">
          {current && (
            <>
              <SheetHeader><SheetTitle>{current.tramite_label}</SheetTitle></SheetHeader>
              <div className="mt-2 flex items-center gap-2"><span className="font-mono text-xs">{current.folio}</span><StatusBadge status={current.status} /></div>
              {current.status === "emitido" && (
                <Card className="mt-4 border-emerald-200 bg-emerald-50/50" data-testid="expediente-constancia">
                  <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileCheck2 className="h-5 w-5 text-emerald-700" />Documento oficial emitido</CardTitle></CardHeader>
                  <CardContent className="flex gap-4 items-start">
                    <div className="bg-white p-2 rounded-md border"><QRCodeSVG value={`${window.location.origin}/verificar/${current.folio}`} size={96} /></div>
                    <div className="text-xs space-y-1 min-w-0">
                      <div>Firmado por: {current.signature?.signer}</div>
                      <div className="font-mono break-all">Firma: {current.signature?.signature.slice(0, 40)}…</div>
                      <Button size="sm" variant="outline" className="mt-1" onClick={() => window.open(`/verificar/${current.folio}`, "_blank")}>Verificar</Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              <div className="mt-5"><Timeline items={current.timeline} labels={TL} /></div>
              <div className="mt-5 space-y-2">
                <div className="text-sm font-medium">Expediente digital</div>
                {Object.entries(current.fields || {}).map(([k, v]) => <Row key={k} label={k.replace(/_/g, " ")}><span className="text-sm">{v}</span></Row>)}
                {current.documents?.map((d) => <Row key={d.doc_id} label={d.name}><Hash value={d.sha256} n={8} /></Row>)}
              </div>
              {current.status === "emitido" && <div className="mt-5"><LegalPanel data_hash={current.data_hash} nom151={current.nom151} anchor={current.anchor} signature={current.signature} /></div>}
              {current.status === "pago_pendiente" && <Button className="w-full mt-5" onClick={() => setCheckout({ kind: "tramite", ref: current.folio })}>Pagar {money(current.cost)}</Button>}
            </>
          )}
        </SheetContent>
      </Sheet>

      <CheckoutDialog open={!!checkout} request={checkout} onOpenChange={(o) => !o && setCheckout(null)} onDone={() => mine.reload()} />
    </div>
  );
}
